import React, { useState } from 'react';
import { Header } from '../../components/common/Header';
import { Footer } from '../../components/common/Footer';
import { QrCode, ArrowLeft, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

export function ReferralCodePage() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [name, setName] = useState('');
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);

  const handleGenerateQRCode = () => {
    if (!name.trim()) return;
    
    setIsGenerating(true);
    
    // タイムスタンプを含めた暗号化
    const timestamp = Date.now();
    // 日本語文字を含む文字列を正しくエンコードし、完全な暗号化文字列を保持
    const encodedName = btoa(encodeURIComponent(`${name.trim()}_${timestamp}`));
    // 表示用に短いコードを生成
    const displayCode = encodedName.substring(0, 8);
    
    // 紹介用URL生成
    const baseUrl = window.location.origin;
    const referralUrl = `${baseUrl}/signup?ref=${encodedName}`;
    
    // 状態を更新
    setGeneratedCode(displayCode);
    setGeneratedUrl(referralUrl);
    
    setTimeout(() => {
      setIsGenerating(false);
    }, 1000);
  };

  // QRコードをダウンロードする関数
  const downloadQRCode = () => {
    if (!generatedUrl) return;
    
    try {
      // SVGからCanvasに変換してダウンロード
      const svgElement = document.getElementById('referral-qrcode');
      if (!svgElement) {
        console.error('QRコード要素が見つかりません');
        alert('QRコードが見つかりません。しばらく待ってから再度お試しください。');
        return;
      }

      // SVGのサイズを取得
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.error('Canvas context を取得できません');
        alert('ダウンロードに失敗しました。ブラウザを更新して再度お試しください。');
        return;
      }

      // QRコードのサイズを設定（高解像度で出力）
      const size = 400;
      canvas.width = size;
      canvas.height = size;

      // 背景を白に設定
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, size, size);

      const img = new Image();
      img.onload = () => {
        // SVGをCanvasに描画
        ctx.drawImage(img, 0, 0, size, size);
        
        // CanvasからPNG形式のデータURLを取得
        const pngUrl = canvas.toDataURL('image/png');
        
        // ダウンロードリンクを作成
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `referral-code-${generatedCode}.png`;
        
        // 一時的にDOMに追加してクリック
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        
        console.log('QRコードのダウンロードが完了しました');
      };
      
      img.onerror = () => {
        console.error('QRコード画像の読み込みに失敗しました');
        alert('QRコードの処理に失敗しました。再度お試しください。');
      };
      
      // SVGデータをBlobに変換してImageに設定
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      img.src = url;
      
    } catch (error) {
      console.error('QRコードダウンロードエラー:', error);
      alert('ダウンロードに失敗しました。しばらく待ってから再度お試しください。');
    }
  };

  // デバッグ用のログ
  console.log('downloadQRCode function defined');
    
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 relative">
          <Link to="/signup" className="absolute top-8 left-8 flex items-center text-blue-600 hover:text-blue-800">
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span>戻る</span>
          </Link>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">紹介コードの作成</h1>
          
          <div className="prose prose-blue max-w-none">
         

            <div className="bg-blue-50 p-6 rounded-lg mb-8">
              <h2 className="text-xl font-semibold text-blue-800 mb-4 text-center">あなた専用の紹介コードを作成して、友達や知り合いにHalsystemを紹介しましょう。</h2>
              <ul className="space-y-2 text-blue-700">
              
              </ul>
            </div>

            <div className="max-w-md mx-auto mt-8">
              {!generatedCode ? (
                <form 
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleGenerateQRCode();
                  }}
                  className="space-y-6"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">　
                      あなたのお名前
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 block w-full rounded-md p-2 border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="鈴木 一郎"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      紹介情報は暗号化された後、QRコードとURLが生成されます、紹介者名の情報は第三者に知られる事はありません。
                    </p>
                  </div>
                  
                  <div className="flex justify-center">
                    <button
                      type="submit"
                      disabled={isGenerating || !name.trim()}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
                    >
                      <QrCode className="w-5 h-5" />
                      <span>{isGenerating ? 'QRコード生成中...' : 'QRコードを作成'}</span>
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6 text-center">
                  <div className="bg-gray-100 p-8 rounded-lg inline-block mx-auto">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <QRCodeSVG
                        id="referral-qrcode"
                        value={generatedUrl || ''}
                        size={200}
                        bgColor={"#ffffff"}
                        fgColor={"#000000"}
                        level={"H"}
                        includeMargin={true}
                      />
                    </div>
                    <button
                      onClick={downloadQRCode}
                      className="mt-2 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors w-full"
                    >
                      <Download className="w-4 h-4" />
                      <span>QRコードをダウンロード</span>
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700">あなたの紹介コード</h3>
                      <p className="mt-1 text-lg font-bold text-blue-600">{generatedCode}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-gray-700">紹介用URL</h3>
                      <div className="mt-1 flex items-center justify-center">
                        <input
                          type="text"
                          value={generatedUrl || ''}
                          readOnly
                          className="block w-full p-2 rounded-md border border-gray-300 bg-gray-50 text-sm"
                        />
                        <button
                          onClick={() => {
                            if (generatedUrl) {
                              navigator.clipboard.writeText(generatedUrl);
                              alert('URLをコピーしました');
                            }
                          }}
                          className="ml-2 px-4 py-2 w-[100px] bg-gray-300 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                          コピー
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600">
                      このQRコードまたはURLを友達に共有して、サービスへの登録を促しましょう。
                      友達がこのリンクから登録すると、あなたと友達の両方にAIクレジットが付与されます。
                    </p>
                    
                    <button
                      onClick={() => {
                        setGeneratedCode(null);
                        setGeneratedUrl(null);
                        setName('');
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                      別の紹介コードを作成
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}